import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, of, Subject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { DocumentTypeMimeTypes } from '../../../core/model/const/document-type-mime-types';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { BuildingDocumentType, RentalObjectDocumentType } from '../../../rent/model/const/documentEnums';
import { LettingAdvertismentPlatformType } from '../../model/const/enums';
import { LettingAdvertisment } from '../../model/letting-advertisment';
import { DhMarketOptions } from '../../model/platform-options';
import { LettingShowing } from '../../model/letting-showing';
import { IUploadedDocument } from '../../model/uploaded-document';
import { LettingAdvertismentPremisesFormCommandHandlerResolver } from '../../service/command/letting-advertisment-premises-form-command-handler-resolver.service';
import { LettingAdvertismentPremisesService } from '../../service/letting-advertisment-premises.service';
import { LettingAdvertismentMarketAreaComponent } from '../letting-advertisment-market-area/letting-advertisment-market-area.component';
import { LettingAdvertismentMarketComponent } from '../letting-advertisment-market/letting-advertisment-market.component';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-letting-advertisment-form',
    templateUrl: 'letting-advertisment-premises-form.component.html',
    styleUrls: ['letting-advertisment-premises-form.component.scss'],
    providers: [
        { provide: DataService, useExisting: LettingAdvertismentPremisesService },
        DeleteFormCommandHandler,
        LettingAdvertismentPremisesFormCommandHandlerResolver,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LettingAdvertismentPremisesFormComponent extends FormComponentBase<LettingAdvertisment> {
  constructor(
    private readonly _dataService: LettingAdvertismentPremisesService,
    private readonly _cf: FormControlExFactory,
    private readonly _fb: FormBuilder,
    private readonly _route: ActivatedRoute,
    private readonly _accountService: UserAccountService,
    commandDispatcher: CommandDispatcher,
    handlerResolver: LettingAdvertismentPremisesFormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    private readonly _languageService: LanguageService
  ) {
    super(_route, _dataService, messageService, navigation, commandDispatcher, handlerResolver);
  }

  protected pdfOrImageMimes = DocumentTypeMimeTypes.PDF().concat(DocumentTypeMimeTypes.WebImage());
  protected imageMimes = DocumentTypeMimeTypes.WebImage();

  protected tenants: LettingShowing[];

  protected RentalObjectDocumentType = RentalObjectDocumentType;
  protected BuildingDocumentType = BuildingDocumentType;

  protected createModel(): LettingAdvertisment {
    var model = new LettingAdvertisment();
    return model;
  }

  platformSelection: SelectionItem[] = [];

  private willBeSentToPlatform: Subject<void>;
  protected currentLang: string = this._languageService.currentCultureLang;

  @ViewChild('marketform') marketform: LettingAdvertismentMarketComponent;
  @ViewChild('marketareaform') marketareaform: LettingAdvertismentMarketAreaComponent;

  protected getFormGroup(): FormGroup {
    let readOnly = this.dataContext.approvedAt || !!this.dataContext.sentToPlatformAt ? true : false;
    let group = this._fb.group({
      id: this._cf.createHidden(this.dataContext.id),
      rentalObjectId: this._cf.createHidden(this.dataContext.rentalObjectId),
      rentalObjectName: this._cf.createWithOptions('COMMON.RENTAL_OBJECT', this.dataContext.rentalObjectName, { isReadonly: true }),
      platform: this._cf.createWithOptions('LETTING.ADVERTISMENT.PLATFORM', null, { required: true, isReadonly: readOnly }),
      approvedAt: this._cf.createReadonly('LETTING.ADVERTISMENT.APPROVED_AT', this.dataContext.approvedAt),
      rentalObjectDocuments: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.INTERIOR_PHOTO',
        this.transformDocument(this.dataContext.rentalObjectDocuments),
        { isReadonly: readOnly }
      ),
      floorPlanDocuments: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.FLOOR_PLAN',
        this.transformDocument(this.dataContext.floorPlanDocuments),
        { isReadonly: readOnly }
      ),
      buildingSurroundingsPhotos: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.SURROUNDINGS_PHOTO',
        this.transformDocument(this.dataContext.buildingSurroundingsPhotos),
        { isReadonly: readOnly }
      ),
      dhMarketArea: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.DHMARKET.AREA',
        this.dataContext.dhMarketArea ? this.dataContext.dhMarketArea : '',
        { isReadonly: readOnly }
      ),
    });
    this.AddSubscription(group.controls['platform'].valueChanges.subscribe({ next: value => this.platformSelected(value) }));
    group.controls['platform'].setValue(this.dataContext.platform);

    return group;
  }

  dhMarketOptions: DhMarketOptions;
  get dhMarketSelected(): boolean {
    return this.form.controls['platform'].value == LettingAdvertismentPlatformType.DhMarket;
  }

  platformSelected(platform: LettingAdvertismentPlatformType): void {
    switch (platform) {
      case LettingAdvertismentPlatformType.DhMarket: {
        this._dataService.getDhMarketOptions().subscribe({
          next: res => (this.dhMarketOptions = res),
          error: () => this.messageService.showWarning('LETTING.ADVERTISMENT.DHMARKET.ERROR_GET_OPTIONS'),
        });
      }
    }
  }

  protected init(advertisment: LettingAdvertisment) {
    this.AddSubscription(
      combineLatest([
        this._accountService.current,
        advertisment.isNew
          ? this._route.paramMap.pipe(
              switchMap(map => {
                let id = map.get('rentalObjectId');
                return id ? this._dataService.getDefaultsForRentalObject(id) : of(this.createModel());
              })
            )
          : of(advertisment),
      ]).subscribe({
        next: ([account, model]) => {
          if (account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)) {
            this.platformSelection.push(
              new SelectionItem('LETTING.ADVERTISMENT.PLATFORMS.DHMARKET', LettingAdvertismentPlatformType.DhMarket)
            );
            if (model.rentalObjectId) {
              this.AddSubscription(
                this._dataService.getRentalObjectTentants(model.rentalObjectId).subscribe({ next: res => (this.tenants = res) })
              );
            }
          }

          super.init(model);
        },
      })
    );

    this.currentLang = this._languageService.currentCultureLang;
    this.AddSubscription(
      this._languageService.onLangChange.subscribe({
        next: e => {
          this.checkLangCode(e.lang);
        },
      })
    );
  }

  checkLangCode(lang: string) {
    switch (lang) {
      case 'sv':
        this.currentLang = 'sv-SE';
        break;
      case 'en':
        this.currentLang = 'en-GB';
        break;
      case 'da':
        this.currentLang = 'da-DK';
        break;
      case 'fi':
        this.currentLang = 'fi-FI';
        break;
      default:
        this.currentLang = 'sv-SE';
        break;
    }
  }

  prepareSave(): LettingAdvertisment {
    let model = super.prepareSave();

    if (this.dhMarketSelected) {
      Object.assign(model, this.marketform.form.value);
      Object.assign(model, this.marketareaform.form.value);
    }

    model.dhMarketArea = model.dhMarketArea ? model.dhMarketArea : null;

    return model;
  }

  saveAndSubmit(): void {
    if (!this.dataContext.id || this.form.dirty || this.marketform?.form.dirty || this.marketareaform?.form.dirty) {
      this.willBeSentToPlatform = new Subject<void>();
      this.onSubmit();
    } else {
      this.sendToPlatform(this.dataContext.id);
    }
  }

  onSubmit() {
    if (this.dataContext.approvedAt) {
      this.willBeSentToPlatform = null;
      return;
    }

    if (this.dhMarketSelected && (this.marketform.form.invalid || this.marketareaform.form.invalid)) {
      this.messageService.showError('LETTING.ADVERTISMENT.ERRORS.PLEASE_CHECK_THE_FORM');
      this.marketform.form.markAllAsTouched();
      this.marketareaform.form.markAllAsTouched();
      this.willBeSentToPlatform = null;
      return;
    }

    // not allowed to save a pristine main form, so dirty it
    if (this.marketform?.form.dirty || this.marketareaform?.form.dirty) {
      this.form.markAsDirty();
    }

    if (this.isSaving || !this.form || this.form.invalid || this.form.pristine) {
      // disable send to platform, but let super.onsubmit handle the form stuff
      this.willBeSentToPlatform = null;
    }

    return super.onSubmit();
  }

  protected override formSubmittedNew(id: string) {
    if (this.willBeSentToPlatform) {
      this.sendToPlatform(id);
    }
    this.formSubmitted();
  }

  protected override formSubmittedChange(response?: any) {
    if (this.willBeSentToPlatform) {
      this.sendToPlatform(this.dataContext.id);
      this.willBeSentToPlatform.pipe(take(1)).subscribe({
        next: () => location.reload(),
      });
    }
    this.formSubmitted();
  }

  private sendToPlatform(id: string) {
    this._dataService.sendToPlatform(id).subscribe({
      next: () => {
        if (this.willBeSentToPlatform) {
          this.willBeSentToPlatform.next();
        } else {
          this.navigation.reloadCurrentRoute();
        }
      },
      error: () => this.willBeSentToPlatform?.complete(),
    });
  }

  cancel() {
    this.navigation.navigate(['my-property', 'premises', 'edit', this.dataContext.rentalObjectId]);
  }

  protected navigateAfterSave(id: any) {
    if (this.willBeSentToPlatform) {
      this.willBeSentToPlatform.pipe(take(1)).subscribe({
        next: () => {
          this.navigation.navigateChild(['edit', id]);
        },
      });
    } else {
      this.navigation.navigateChild(['edit', id]);
    }
  }

  private transformDocument(documents: IUploadedDocument[]) {
    return documents.map(document => ({ id: document.connectionId, documentId: document.id, documentTitle: document.title }));
  }
}
